<template>
  <div :class="$attrs.class">
    <component
      :is="form.Field"
      :name="name"
      :class="$attrs.class"
      :validators="validators"
    >
      <template #default="formProps">
        <div class="form-group">
          <label v-if="label" class="form-label" :for="inputId">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
          </label>
          <input
            :id="inputId"
            ref="inputRef"
            :value="storeFormProps(formProps)"
            type="text"
            class="form-input"
            :class="{ 'form-input--error': error(formProps) }"
            :disabled="!!disabled"
            :required="!!required"
            :autocomplete="autocomplete"
            :placeholder="placeholder"
            :aria-label="label"
            :aria-required="!!required"
            :aria-invalid="!!error(formProps)"
            :aria-describedby="
              error(formProps) ? `${inputId}-error` : undefined
            "
            @input="(event) => handleInput(event, formProps)"
            @blur="
              () => {
                try {
                  formProps?.field?.handleBlur?.()
                } catch (error) {
                  console.error('Error in blur handler:', error)
                }
              }
            "
            @focus="
              () => {
                try {
                  formProps?.field?.handleFocus?.()
                } catch (error) {
                  console.error('Error in focus handler:', error)
                }
              }
            "
          />
          <span
            v-if="error(formProps)"
            :id="`${inputId}-error`"
            class="form-error"
            role="alert"
          >
            {{ error(formProps) }}
          </span>
        </div>
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import type { z } from 'zod'

export interface ParsedAddressData {
  country?: string
  admin_area?: string
  locality?: string
  zipcode?: string
  street?: string
  street_number?: string
  lat?: number
  lng?: number
  additional_information?: string | null
  address_components?: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
}

const props = defineProps<{
  form: any // The form instance from @tanstack/vue-form
  name: string
  label?: string
  disabled?: boolean
  required?: boolean
  id?: string
  autocomplete?: string
  placeholder?: string
  validators?: Record<string, z.ZodType<any>>
  showParsedData?: boolean // Whether to show parsed address data in UI
}>()

const emit = defineEmits<{
  'address-selected': [data: ParsedAddressData]
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const autocompleteInstance = ref<google.maps.places.Autocomplete | null>(null)
const isGoogleMapsLoaded = ref(false)
const formFieldRef = ref<any>(null)
const isMounted = ref(false)
const parsedAddressData = ref<ParsedAddressData | null>(null)
const currentFormPropsRef = ref<any>(null)
const googleMapsCheckInterval = ref<NodeJS.Timeout | null>(null)

// Must be called at top level of setup function
const config = useRuntimeConfig()
const { locale } = useI18n()

const inputId =
  props.id || `address-autocomplete-${Math.random().toString(36).substr(2, 9)}`

const storeFormProps = (formProps: any) => {
  if (formProps?.field) {
    currentFormPropsRef.value = formProps
    formFieldRef.value = formProps.field
  }
  return formProps?.field?.state?.value || ''
}

const error = (formProps: any) => {
  if (!formProps?.field?.state?.meta) return undefined
  const errors = formProps.field.state.meta.errors
  if (!errors?.length) return undefined
  const first = errors[0]
  return typeof first === 'string' ? first : first?.message
}

/**
 * Parses Google Maps Places API address_components into structured format
 */
const parseAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
  geometry: google.maps.places.PlaceGeometry | undefined
): ParsedAddressData => {
  const parsed: ParsedAddressData = {
    address_components: addressComponents?.map((comp) => ({
      long_name: comp.long_name,
      short_name: comp.short_name,
      types: comp.types || [],
    })),
  }

  if (!addressComponents) {
    return parsed
  }

  // Extract address components by type
  for (const component of addressComponents) {
    const types = component.types || []

    if (types.includes('street_number')) {
      parsed.street_number = component.long_name
    } else if (types.includes('route')) {
      parsed.street = component.long_name
    } else if (types.includes('locality')) {
      parsed.locality = component.long_name
    } else if (types.includes('administrative_area_level_1')) {
      parsed.admin_area = component.short_name
    } else if (types.includes('country')) {
      parsed.country = component.short_name
    } else if (types.includes('postal_code')) {
      parsed.zipcode = component.long_name
    }
  }

  // Extract coordinates
  if (geometry?.location) {
    parsed.lat =
      typeof geometry.location.lat === 'function'
        ? geometry.location.lat()
        : (geometry.location as any).lat
    parsed.lng =
      typeof geometry.location.lng === 'function'
        ? geometry.location.lng()
        : (geometry.location as any).lng
  }

  parsed.additional_information = null

  return parsed
}

const handleInput = (event: Event, formProps: any) => {
  try {
    const target = event.target as HTMLInputElement
    if (!target) return

    // Capture formProps.field at the time of the event to avoid stale closures
    const currentFormProps = formProps
    if (!currentFormProps?.field) {
      // If field is not available, just return - input value is already updated
      return
    }

    const field = currentFormProps.field
    if (!field || typeof field.handleChange !== 'function') {
      return
    }

    // Store field reference before calling handleChange
    formFieldRef.value = field
    currentFormPropsRef.value = currentFormProps

    // Call handleChange with comprehensive error handling
    // TanStack Form's handleChange might throw if field state is invalid
    // We catch and log but don't rethrow to prevent app crashes
    try {
      field.handleChange(target.value)
    } catch (handleChangeError: any) {
      // Log but don't throw - prevent app crash
      // The error "Cannot set properties of null" happens inside TanStack Form
      // when the field state becomes invalid (e.g., component unmounting, form reset)
      console.error('Error in field.handleChange:', handleChangeError)
      // Silently fail - the input value is already updated by the user
    }
  } catch (error) {
    // Outer catch for any other unexpected errors
    console.error('Error in input handler:', error)
  }
}

const loadGoogleMaps = () => {
  return new Promise<void>((resolve, reject) => {
    const hasWindow = typeof window !== 'undefined'

    if (hasWindow && window?.google?.maps?.places) {
      isGoogleMapsLoaded.value = true
      resolve()
      return
    }

    const scriptId = 'google-maps-places-script'
    if (document.getElementById(scriptId)) {
      // Script already loading/loaded
      googleMapsCheckInterval.value = setInterval(() => {
        // Check if component is still mounted and window exists
        if (!isMounted.value || typeof window === 'undefined') {
          if (googleMapsCheckInterval.value) {
            clearInterval(googleMapsCheckInterval.value)
            googleMapsCheckInterval.value = null
          }
          return
        }
        if (window?.google?.maps?.places) {
          if (googleMapsCheckInterval.value) {
            clearInterval(googleMapsCheckInterval.value)
            googleMapsCheckInterval.value = null
          }
          isGoogleMapsLoaded.value = true
          resolve()
        }
      }, 100)
      return
    }

    const apiKey = config.public.googleMapsApiKey
    if (!apiKey) {
      console.warn('Google Maps API key not configured')
      reject(new Error('Google Maps API key not configured'))
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${locale.value}`
    script.async = true
    script.defer = true
    script.onload = () => {
      isGoogleMapsLoaded.value = true
      resolve()
    }
    script.onerror = () => {
      reject(new Error('Failed to load Google Maps API'))
    }
    document.head.appendChild(script)
  })
}

// Watch for formProps updates to ensure we capture initial values
watchEffect(() => {
  if (
    currentFormPropsRef.value?.field?.state?.value &&
    inputRef.value &&
    autocompleteInstance.value
  ) {
    // Update input value if form field value changes and input exists
    const currentValue = inputRef.value.value
    const formValue = currentFormPropsRef.value.field.state.value
    if (currentValue !== formValue) {
      inputRef.value.value = formValue
    }
  }
})

onMounted(async () => {
  isMounted.value = true
  await nextTick()
  if (!inputRef.value) return

  try {
    await loadGoogleMaps()

    const hasWindowGoogle = typeof window !== 'undefined'

    if (!hasWindowGoogle || !window.google?.maps?.places || !isMounted.value) {
      console.error('Google Maps Places API not available')
      return
    }

    if (!inputRef.value) return

    // Store form field reference if available
    if (currentFormPropsRef.value?.field) {
      formFieldRef.value = currentFormPropsRef.value.field
    }

    // Initialize Autocomplete
    autocompleteInstance.value = new google.maps.places.Autocomplete(
      inputRef.value,
      {
        types: ['address'],
        fields: ['formatted_address', 'address_components', 'geometry'],
      }
    )

    // Remove Google Maps default placeholder
    if (inputRef.value) {
      inputRef.value.setAttribute('placeholder', props.placeholder || '')
    }

    // Set initial value if it exists in the form field
    await nextTick()
    const initialValue = currentFormPropsRef.value?.field?.state?.value
    if (initialValue && inputRef.value) {
      inputRef.value.value = initialValue
    }

    // Listen for place selection
    autocompleteInstance.value.addListener('place_changed', () => {
      if (!isMounted.value) return

      const place = autocompleteInstance.value?.getPlace()
      const currentInput = inputRef.value
      const currentField = formFieldRef.value

      if (!place?.formatted_address || !currentInput) return

      // Parse address components
      const parsedData = parseAddressComponents(
        place.address_components,
        place.geometry
      )
      parsedAddressData.value = parsedData

      // Emit parsed address data
      emit('address-selected', parsedData)

      // Update form field if handler is available
      if (currentField?.handleChange) {
        currentField.handleChange(place.formatted_address)
        // Also update input value directly to ensure sync
        if (currentInput) {
          currentInput.value = place.formatted_address
        }
      } else if (currentInput) {
        // Fallback: update input value directly
        // This will trigger the @input handler which will update the form field
        currentInput.value = place.formatted_address
        const inputEvent = new Event('input', { bubbles: true })
        currentInput.dispatchEvent(inputEvent)
      }
    })
  } catch (error) {
    console.error('Error initializing Google Maps Autocomplete:', error)
  }
})

onUnmounted(() => {
  isMounted.value = false
  // Clear any pending interval checks
  if (googleMapsCheckInterval.value) {
    clearInterval(googleMapsCheckInterval.value)
    googleMapsCheckInterval.value = null
  }
  if (autocompleteInstance.value) {
    // Check if window and google.maps exist before clearing listeners
    if (typeof window !== 'undefined' && window.google?.maps?.event) {
      google.maps.event.clearInstanceListeners(autocompleteInstance.value)
    }
    autocompleteInstance.value = null
  }
  formFieldRef.value = null
})

defineExpose({
  inputRef,
  focusInput: () => {
    if (inputRef.value) {
      inputRef.value.focus()
    }
  },
})
</script>

<style lang="postcss" scoped>
.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.required-asterisk {
  @apply text-red-500 ml-1;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500;
  @apply text-black;
}

.form-input--error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply text-sm text-red-500;
}

.address-data-display {
  @apply mt-4 p-4 bg-gray-50 rounded-md border border-gray-200;
}

.address-data-title {
  @apply text-sm font-semibold text-gray-700 mb-3;
}

.address-data-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-2 mb-3;
}

.address-data-item {
  @apply flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2;
}

.address-data-label {
  @apply text-xs font-medium text-gray-600 sm:w-24;
}

.address-data-value {
  @apply text-sm text-gray-900;
}

.address-components-details {
  @apply mt-3;
}

.address-components-summary {
  @apply text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-800;
}

.address-components-json {
  @apply mt-2 p-3 bg-white rounded border border-gray-200 text-xs overflow-x-auto;
  @apply font-mono text-gray-700;
}
</style>
